import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { ArrowLeft, Mail, Lock, User, Eye, EyeOff, CheckCircle, XCircle, Shield, Building2, ChevronDown, X } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

const EmailRegisterPage = () => {
  const navigate = useNavigate();
  const { register, login } = useAuth();
  const dropdownRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    departments: []
  });
  const [departments, setDepartments] = useState([]);
  const [showDepartmentDropdown, setShowDepartmentDropdown] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingDepartments, setLoadingDepartments] = useState(true);
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  // Fetch departments on component mount
  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const response = await axios.get('/departments');
        setDepartments(response.data);
      } catch (error) {
        console.error('Failed to fetch departments:', error);
        toast.error('Failed to load departments');
      } finally {
        setLoadingDepartments(false);
      }
    };
    fetchDepartments();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDepartmentDropdown(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  // Handle department selection toggle
  const toggleDepartment = (deptId) => {
    setFormData(prev => {
      const isSelected = prev.departments.includes(deptId);
      return {
        ...prev,
        departments: isSelected
          ? prev.departments.filter(id => id !== deptId)
          : [...prev.departments, deptId]
      };
    });
  };

  // Remove a department from selection
  const removeDepartment = (deptId) => {
    setFormData(prev => ({
      ...prev,
      departments: prev.departments.filter(id => id !== deptId)
    }));
  };

  // Get department name by ID
  const getDepartmentName = (deptId) => {
    const dept = departments.find(d => d.id === deptId);
    return dept?.name || 'Unknown';
  };

  // Password strength validation
  const passwordChecks = {
    length: formData.password.length >= 8,
    uppercase: /[A-Z]/.test(formData.password),
    lowercase: /[a-z]/.test(formData.password),
    number: /\d/.test(formData.password)
  };

  const isPasswordStrong = Object.values(passwordChecks).every(Boolean);
  const passwordsMatch = formData.password === formData.confirmPassword && formData.confirmPassword.length > 0;
  const hasDepartmentsSelected = formData.departments.length > 0;

  const handleRegister = async (e) => {
    e.preventDefault();
    
    // Validate name
    if (formData.name.trim().length < 2) {
      toast.error('Please enter your full name');
      return;
    }

    // Validate email
    if (!formData.email.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }

    // Validate departments
    if (!hasDepartmentsSelected) {
      toast.error('Please select at least one department/exam');
      return;
    }

    // Validate password strength
    if (!isPasswordStrong) {
      toast.error('Password does not meet requirements');
      return;
    }

    // Validate passwords match
    if (!passwordsMatch) {
      toast.error('Passwords do not match');
      return;
    }

    // Validate terms agreement
    if (!agreedToTerms) {
      toast.error('Please agree to Terms of Service and Privacy Policy');
      return;
    }

    setLoading(true);
    try {
      // Register the user with departments
      await register(formData.email, formData.password, formData.name, formData.departments);
      toast.success('Registration successful! Logging you in...');
      
      // Auto-login after registration
      await login(formData.email, formData.password);
      navigate('/dashboard');
    } catch (error) {
      console.error('Registration failed:', error);
      toast.error(error.response?.data?.detail || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const PasswordCheck = ({ passed, label }) => (
    <div className={`flex items-center gap-2 text-xs ${passed ? 'text-green-600' : 'text-gray-400'}`}>
      {passed ? <CheckCircle className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
      <span>{label}</span>
    </div>
  );

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-blue-100 p-4">
      <Card className="w-full max-w-md shadow-2xl">
        <CardHeader className="space-y-3 pb-4">
          <button 
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-2"
            data-testid="back-to-home-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </button>
          <div className="flex justify-center mb-2">
            <img 
              src="https://customer-assets.emergentagent.com/job_cgda-exam-prep/artifacts/bek6ttpm_logo.png" 
              alt="Departmental Exam"
              className="h-16 sm:h-20 md:h-24 object-contain"
            />
          </div>
          <CardTitle className="text-2xl sm:text-3xl font-bold text-center text-gray-800">
            Create Account
          </CardTitle>
          <CardDescription className="text-center text-sm sm:text-base">
            Sign up with email and password to start your preparation
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleRegister} className="space-y-4">
            {/* Full Name */}
            <div className="space-y-2">
              <label htmlFor="name" className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <User className="w-4 h-4" />
                Full Name <span className="text-red-500">*</span>
              </label>
              <Input
                id="name"
                name="name"
                type="text"
                placeholder="Enter your full name"
                value={formData.name}
                onChange={handleChange}
                className="h-11 text-sm border-2 border-gray-200 focus:border-blue-500 rounded-xl"
                required
                data-testid="name-input"
              />
            </div>

            {/* Email */}
            <div className="space-y-2">
              <label htmlFor="email" className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <Mail className="w-4 h-4" />
                Email Address <span className="text-red-500">*</span>
              </label>
              <Input
                id="email"
                name="email"
                type="email"
                placeholder="your.email@example.com"
                value={formData.email}
                onChange={handleChange}
                className="h-11 text-sm border-2 border-gray-200 focus:border-blue-500 rounded-xl"
                required
                data-testid="email-input"
              />
            </div>

            {/* Department/Exam Selection */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <Building2 className="w-4 h-4" />
                Select Department/Exam <span className="text-red-500">*</span>
              </label>
              
              {loadingDepartments ? (
                <div className="h-11 flex items-center justify-center text-sm text-gray-500 border-2 border-gray-200 rounded-xl">
                  Loading departments...
                </div>
              ) : departments.length === 0 ? (
                <div className="h-11 flex items-center justify-center text-sm text-gray-500 border-2 border-gray-200 rounded-xl">
                  No departments available
                </div>
              ) : (
                <div className="relative" ref={dropdownRef}>
                  {/* Selected Departments Display */}
                  <div 
                    onClick={() => setShowDepartmentDropdown(!showDepartmentDropdown)}
                    className={`min-h-11 p-2 flex flex-wrap gap-2 items-center cursor-pointer border-2 rounded-xl transition-colors ${
                      showDepartmentDropdown ? 'border-blue-500' : 'border-gray-200'
                    } ${formData.departments.length === 0 ? 'text-gray-400' : ''}`}
                    data-testid="department-selector"
                  >
                    {formData.departments.length === 0 ? (
                      <span className="text-sm">Select one or more departments...</span>
                    ) : (
                      formData.departments.map(deptId => (
                        <span 
                          key={deptId}
                          className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full"
                          data-testid={`selected-dept-${deptId}`}
                        >
                          {getDepartmentName(deptId)}
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              removeDepartment(deptId);
                            }}
                            className="hover:bg-blue-200 rounded-full p-0.5"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      ))
                    )}
                    <ChevronDown className={`w-4 h-4 ml-auto text-gray-400 transition-transform ${showDepartmentDropdown ? 'rotate-180' : ''}`} />
                  </div>

                  {/* Dropdown Options */}
                  {showDepartmentDropdown && (
                    <div className="absolute z-50 w-full mt-1 bg-white border-2 border-gray-200 rounded-xl shadow-lg max-h-48 overflow-y-auto">
                      {departments.map(dept => (
                        <div
                          key={dept.id}
                          onClick={() => toggleDepartment(dept.id)}
                          className={`flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-blue-50 transition-colors ${
                            formData.departments.includes(dept.id) ? 'bg-blue-50' : ''
                          }`}
                          data-testid={`dept-option-${dept.id}`}
                        >
                          <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${
                            formData.departments.includes(dept.id) 
                              ? 'bg-blue-600 border-blue-600' 
                              : 'border-gray-300'
                          }`}>
                            {formData.departments.includes(dept.id) && (
                              <CheckCircle className="w-3 h-3 text-white" />
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            {dept.logo ? (
                              <img src={dept.logo} alt={dept.name} className="w-6 h-6 rounded object-cover" />
                            ) : (
                              <Building2 className="w-6 h-6 text-gray-400" />
                            )}
                            <span className="text-sm font-medium text-gray-700">{dept.name}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              
              {formData.departments.length > 0 && (
                <p className="text-xs text-green-600 flex items-center gap-1">
                  <CheckCircle className="w-3 h-3" />
                  {formData.departments.length} department{formData.departments.length > 1 ? 's' : ''} selected
                </p>
              )}
            </div>

            {/* Password */}
            <div className="space-y-2">
              <label htmlFor="password" className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <Lock className="w-4 h-4" />
                Password <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Create a strong password"
                  value={formData.password}
                  onChange={handleChange}
                  className="h-11 text-sm pr-10 border-2 border-gray-200 focus:border-blue-500 rounded-xl"
                  required
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              
              {/* Password Requirements */}
              {formData.password.length > 0 && (
                <div className="grid grid-cols-2 gap-1 pt-1">
                  <PasswordCheck passed={passwordChecks.length} label="8+ characters" />
                  <PasswordCheck passed={passwordChecks.uppercase} label="Uppercase letter" />
                  <PasswordCheck passed={passwordChecks.lowercase} label="Lowercase letter" />
                  <PasswordCheck passed={passwordChecks.number} label="Number" />
                </div>
              )}
            </div>

            {/* Confirm Password */}
            <div className="space-y-2">
              <label htmlFor="confirmPassword" className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <Lock className="w-4 h-4" />
                Confirm Password <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  placeholder="Confirm your password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className={`h-11 text-sm pr-10 border-2 rounded-xl ${
                    formData.confirmPassword.length > 0 
                      ? passwordsMatch 
                        ? 'border-green-500 focus:border-green-500' 
                        : 'border-red-500 focus:border-red-500'
                      : 'border-gray-200 focus:border-blue-500'
                  }`}
                  required
                  data-testid="confirm-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500"
                >
                  {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {formData.confirmPassword.length > 0 && !passwordsMatch && (
                <p className="text-xs text-red-500">Passwords do not match</p>
              )}
            </div>

            {/* Terms and Privacy Agreement */}
            <div className="flex items-start gap-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
              <input
                id="terms"
                type="checkbox"
                checked={agreedToTerms}
                onChange={(e) => setAgreedToTerms(e.target.checked)}
                className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                data-testid="terms-checkbox"
              />
              <label htmlFor="terms" className="text-xs text-gray-700">
                I agree to the{' '}
                <a 
                  href="/terms" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 underline font-medium"
                >
                  Terms of Service
                </a>
                {' '}and{' '}
                <a 
                  href="/privacy" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 underline font-medium"
                >
                  Privacy Policy
                </a>
              </label>
            </div>

            {/* Security Notice */}
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-xs text-amber-800 text-center">
                <Shield className="w-3 h-3 inline mr-1" />
                Your account will be secured with single-device login enforcement
              </p>
            </div>

            <Button
              type="submit"
              className="w-full h-11 text-sm font-semibold bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 rounded-xl shadow-lg"
              disabled={loading || !isPasswordStrong || !passwordsMatch || !agreedToTerms || !hasDepartmentsSelected}
              data-testid="register-submit-btn"
            >
              {loading ? (
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  Creating Account...
                </div>
              ) : (
                'Create Account'
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              Already have an account?{' '}
              <button 
                onClick={() => navigate('/login')}
                className="text-blue-600 font-semibold hover:underline"
                data-testid="login-link"
              >
                Sign in here
              </button>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default EmailRegisterPage;
